import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Tabs, TabsProps } from "antd";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useJobPost } from "../../Hooks/useJobPost";
import JobApplicationListing, {
  JobApplicationListingRef,
} from "./components/jobApplicationListing/JobApplicationListing";
import BulkUploads from "./components/uploads/BulkUploads";
import FileUploads from "./components/uploads/FileUploads";
import JobPostDetailsHeader from "./components/JobPostDetailsHeader";
import Details from "./components/jobPostDetails/Details";
// import StatsAndAnalytics from "./components/StatsAndAnalytics/index";
import useOrganization from "../../Hooks/useOrganization";
import "./index.scss";

const JobPostDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { jobPostId } = useParams();
  const activeTabKey = searchParams.get("tabIndex") || "details";
  const [currentTab, setCurrentTab] = useState<string>(activeTabKey);
  const [applicationCount, setApplicationCount] = useState<number | null>(null);
  const { jobPost, interviewRoundDetails, fetchJobPost, toggleJobStatus } =
    useJobPost(jobPostId || "");
  const wrapperRef = useRef(null);
  const jobApplicationListingRef = useRef<JobApplicationListingRef>(null);
  const { org } = useOrganization();

  const handleTabChange = useCallback(
    (key: string) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("tabIndex", key);

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [location, navigate],
  );

  useEffect(() => {
    if (jobPostId) {
      fetchJobPost(jobPostId);
    } else {
      navigate(-1);
    }
  }, [jobPostId, fetchJobPost, navigate]);

  useEffect(() => {
    if (activeTabKey) {
      setCurrentTab(activeTabKey);
    }
  }, [activeTabKey]);

  // Auto-switch from uploads tab to details when job becomes inactive
  useEffect(() => {
    if (!jobPost?.isActive) {
      const isActiveRequiredTab =
        currentTab === "file-uploads" || currentTab === "bulk-uploads";
      if (isActiveRequiredTab) {
        handleTabChange("details");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPost?.isActive, currentTab, handleTabChange]);

  useEffect(() => {
    if (org && jobPost) {
      if (org._id !== jobPost.orgId) {
        navigate("/", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org, jobPost]);

  const tabItems: TabsProps["items"] = useMemo(() => {
    if (!jobPost) return [];

    const items = [
      {
        key: "details",
        label: "Details",
      },
      {
        key: "applications",
        label: `Applications (${applicationCount ?? jobPost.numberOfApplications ?? 0})`,
      },
    ];

    if (jobPost?.isActive) {
      items.push({
        key: "bulk-uploads",
        label: "Bulk Uploads",
      });
    }

    if (jobPost?.isActive) {
      items.push({
        key: "file-uploads",
        label: "File Uploads",
      });
    }

    // Add Stats & Analytics as the last tab
    // items.push({
    //   key: "stats",
    //   label: "Stats & Analytics",
    // });

    return items;
  }, [jobPost, applicationCount]);

  const getTabChildren = (tab: string) => {
    switch (tab) {
      case "details":
        return (
          <Details
            jobPost={jobPost}
            interviewRoundDetails={interviewRoundDetails || []}
          />
        );
      // case "stats":
      //   return <StatsAndAnalytics jobPost={jobPost} />;
      case "applications":
        return (
          <JobApplicationListing
            ref={jobApplicationListingRef}
            jobPostId={jobPost?._id || ""}
            jobPost={jobPost}
            onTotalCountChange={setApplicationCount}
          />
        );
      case "bulk-uploads":
        return <BulkUploads jobPostId={jobPost?._id || ""} />;
      case "file-uploads":
        return <FileUploads jobDescriptionId={jobPost?._id || ""} />;
      default:
        return null;
    }
  };

  return (
    <section className="job-posts-details-page" ref={wrapperRef}>
      <JobPostDetailsHeader
        jobPost={jobPost}
        currentTab={currentTab}
        handleTabChange={handleTabChange}
        toggleJobStatus={toggleJobStatus}
        fetchJobPost={fetchJobPost}
        jobPostId={jobPostId || ""}
      />
      <Tabs
        style={{ margin: "0px", width: "100%" }}
        activeKey={currentTab}
        items={tabItems}
        onChange={handleTabChange}
        size="small"
        tabBarExtraContent={null}
      />
      <div className="job-post-details-tab-children-wrapper hide-scroll-bar">
        {getTabChildren(currentTab)}
      </div>
    </section>
  );
};

export default JobPostDetails;
